import { getByPath } from '@de/utils/interpolation';
import { DataSourceExtractionError } from '@de/providers/DataSourceManager.types';

/**
 * Facet entry shape exposed to consumers — matches the Offerings API's
 * `_facets.<key>` / `_filterCounts.<key>` payload shape (child SAD ADR-10
 * "Response extraction strategies" table).
 */
export interface FacetEntry {
  readonly name: string;
  readonly slug: string;
  readonly count: number;
}

const FACET_PREFIX = '_facets.';
const FILTER_COUNT_PREFIX = '_filterCounts.';

/**
 * Extract a subset of the response JSON according to `responsePath`.
 *
 * Accepts three path shapes (ADR-10):
 *
 * - **Omitted / empty** — returns the raw response as-is.
 * - **Dot-path** — `'data'`, `'data.organizations'`, delegated to DB-1.2
 *   `getByPath` (which is total — missing keys resolve to `undefined`).
 * - **Facet map** — `'_facets.<key>'` reads
 *   `response._facets[<key>]` and returns it as `{name, slug, count}[]`.
 * - **Filter count map** — `'_filterCounts.<key>'` reads
 *   `response._filterCounts[<key>]` in the same shape.
 *
 * Malformed responses for the facet / filter-count strategies throw
 * {@link DataSourceExtractionError}, which the caller converts to
 * `status: 'error'`. A missing facet key resolves to `undefined`
 * (classified downstream as `empty`) — this mirrors the Offerings API
 * behavior, where a key that produced no matches is simply absent.
 */
export function extractResponsePath(
  response: unknown,
  responsePath?: string,
): unknown {
  if (responsePath === undefined || responsePath.length === 0) {
    return response;
  }

  if (responsePath.startsWith(FACET_PREFIX)) {
    const key = responsePath.slice(FACET_PREFIX.length);
    return extractFacetMap(response, '_facets', key, responsePath);
  }

  if (responsePath.startsWith(FILTER_COUNT_PREFIX)) {
    const key = responsePath.slice(FILTER_COUNT_PREFIX.length);
    return extractFacetMap(response, '_filterCounts', key, responsePath);
  }

  const segments = responsePath
    .split('.')
    .filter((s) => s.length > 0)
    .map((name) => ({ type: 'key' as const, name }));

  return getByPath(response, segments);
}

function extractFacetMap(
  response: unknown,
  container: '_facets' | '_filterCounts',
  key: string,
  responsePath: string,
): readonly FacetEntry[] | undefined {
  if (key.length === 0) {
    throw new DataSourceExtractionError(
      `"${responsePath}" must name a ${container} key (e.g. "${container}.degreeTypes").`,
      responsePath,
    );
  }
  if (response === null || typeof response !== 'object') {
    throw new DataSourceExtractionError(
      `Cannot extract "${responsePath}" from a non-object response.`,
      responsePath,
    );
  }

  const containerValue = (response as Record<string, unknown>)[container];
  if (containerValue === undefined) {
    return undefined;
  }
  if (containerValue === null || typeof containerValue !== 'object') {
    throw new DataSourceExtractionError(
      `Response field "${container}" must be an object.`,
      responsePath,
    );
  }

  const rawEntries = (containerValue as Record<string, unknown>)[key];
  if (rawEntries === undefined) {
    return undefined;
  }
  if (!Array.isArray(rawEntries)) {
    throw new DataSourceExtractionError(
      `"${container}.${key}" must be an array of { name, slug, count }.`,
      responsePath,
    );
  }

  return rawEntries.map((entry, index) =>
    normalizeFacetEntry(entry, container, key, index, responsePath),
  );
}

function normalizeFacetEntry(
  entry: unknown,
  container: string,
  key: string,
  index: number,
  responsePath: string,
): FacetEntry {
  if (entry === null || typeof entry !== 'object') {
    throw new DataSourceExtractionError(
      `"${container}.${key}[${index}]" must be an object.`,
      responsePath,
    );
  }
  const obj = entry as Record<string, unknown>;
  const name = obj.name;
  const slug = obj.slug;
  const count = obj.count;
  if (typeof name !== 'string') {
    throw new DataSourceExtractionError(
      `"${container}.${key}[${index}].name" must be a string.`,
      responsePath,
    );
  }
  if (typeof slug !== 'string') {
    throw new DataSourceExtractionError(
      `"${container}.${key}[${index}].slug" must be a string.`,
      responsePath,
    );
  }
  if (typeof count !== 'number') {
    throw new DataSourceExtractionError(
      `"${container}.${key}[${index}].count" must be a number.`,
      responsePath,
    );
  }
  return { name, slug, count };
}
